import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, MessageSquareText, List, CreditCard, Settings as SettingsIcon, LogOut, Instagram, Shield, Menu, X, FileText } from "lucide-react";

const baseLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/keywords", label: "Keyword Flows", icon: List, testid: "nav-keywords" },
  { to: "/messages", label: "Messages", icon: MessageSquareText, testid: "nav-messages" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const adminExtraLinks = [
  { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" },
  { to: "/admin?tab=blog", label: "Blog CMS", icon: FileText, testid: "nav-blog-admin" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const links = isAdmin
    ? [...baseLinks, ...adminExtraLinks]
    : baseLinks;

  // Close drawer on route change (so tapping a link feels right)
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const SidebarContents = (
    <>
      <Link to="/dashboard" className="h-16 lg:h-20 px-5 lg:px-6 flex items-center gap-3 border-b border-white/10" data-testid="sidebar-logo">
        <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-xl overflow-hidden bg-obsidian shrink-0 flex items-center justify-center">
          <img src="/instavibe-logo.jpg" alt="InstaVibe" className="w-full h-full object-cover scale-[1.35]" />
        </div>
        <span className="font-heading font-black text-xl lg:text-2xl tracking-tighter leading-none">InstaVibe<span className="text-klein">.</span></span>
      </Link>
      <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
        {links.map(({ to, label, icon: Icon, testid }) => {
          // For deep-linked tabs (e.g. /admin?tab=blog), match on the full to-string against the current URL.
          const [path, search = ""] = to.split("?");
          const matchesPath = location.pathname === path;
          const targetTab = new URLSearchParams(search).get("tab") || "";
          const currentTab = new URLSearchParams(location.search).get("tab") || "";
          const isActive = matchesPath && targetTab === currentTab;
          return (
            <NavLink
              key={to}
              to={to}
              end={!search}
              data-testid={testid}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                isActive ? "bg-klein text-white" : "text-white/70 hover:bg-surface/5 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 lg:p-4 border-t border-white/10 space-y-3">
        {user?.ig_connected ? (
          <div className="px-3 py-2 bg-klein/5 border border-klein/20 text-xs" data-testid="ig-connected-badge">
            <div className="flex items-center gap-2 mb-1">
              <Instagram className="w-3.5 h-3.5 text-klein" />
              <span className="font-semibold uppercase tracking-wider text-[10px]">Connected</span>
            </div>
            <div className="font-mono text-white/70 truncate">@{user.ig_username || "account"}</div>
          </div>
        ) : (
          <div className="px-3 py-2 bg-surface/5 text-xs text-white/60" data-testid="ig-disconnected-badge">
            Instagram not connected
          </div>
        )}
        <div className="text-xs">
          <div className="font-semibold truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="overline mt-1">{user?.subscription_tier || "free"} plan</div>
        </div>
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-surface/5 transition-colors" data-testid="sidebar-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-bone text-white lg:flex">
      {/* Mobile top bar (hidden on lg+) */}
      <header className="lg:hidden sticky top-0 z-40 h-14 px-3 flex items-center justify-between border-b border-white/10 bg-bone/95 backdrop-blur-md" data-testid="mobile-top-bar">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="p-2 -ml-2 text-white/80 hover:text-white"
          data-testid="mobile-menu-open"
        >
          <Menu className="w-6 h-6" />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          <div className="w-8 h-8 rounded-lg overflow-hidden bg-obsidian shrink-0 flex items-center justify-center">
            <img src="/instavibe-logo.jpg" alt="InstaVibe" className="w-full h-full object-cover scale-[1.35]" />
          </div>
          <span className="font-heading font-black text-lg tracking-tighter leading-none">InstaVibe<span className="text-klein">.</span></span>
        </Link>
        <span className="w-9" />{/* spacer for visual centering */}
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/10 bg-surface flex-col" data-testid="app-sidebar">
        {SidebarContents}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm"
            data-testid="mobile-drawer-overlay"
          />
          <aside className="absolute left-0 top-0 bottom-0 w-[82%] max-w-xs bg-surface border-r border-white/10 flex flex-col animate-fade-up" data-testid="mobile-drawer-panel">
            <div className="flex items-center justify-end p-3 border-b border-white/10">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="p-2 text-white/80 hover:text-white"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            {SidebarContents}
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
